import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { Button as AriaButton, Dialog, DialogTrigger, Popover } from 'react-aria-components'
import { Avatar, Button, Icon } from '@bloo-team/ui'
import { ChevronRight, Cog, Copy, SlidersHorizontal } from '@bloo-team/ui/icons'
import type { CmAuditEntry, CmRelation, CmScope, CmStatus, CmTimelineEvent, CmTimelineEventKind, CmTimelineKind, CmWorkspace } from '@bloo-team/api-client'
import {
  actorCounts, actorLabel, assignmentText, auditDetail, auditEntity, auditLabel, collapseRuns, dispositionChangeText, editedFields, eventCopyLine, eventTint, isSystemActor, isTicketNote, timelineDays,
  useCmAuditIndex, useCmPeople, useCmRelatedCases, useCmTimeline, type TimelineRow,
} from '../../lib/case-activity'
import { caseLabel, dispositionLabel, sevKey, statusLabel } from '../../lib/case-mgmt'
import { absTime } from '../../lib/local-time'
import { copyText } from '../signal/panel-utils'

/**
 * A case's Timeline tab — the rail (docs/cases.md §Case page · Timeline).
 * One merged feed, newest first (`GET /cases/{id}/timeline`), paged by
 * cursor with Load more, on a vertical rail like the Notes tab's: a node per
 * event whose shape tells its kind. A slim column filters by Kind (on the
 * server; counts from the case workspace where it has them) and by Who (in
 * the console, over the loaded pages). Days fold — Today and Yesterday open,
 * older ones folded. An event is one line; selecting it opens it in place
 * with what it holds and Copy. Audit rows are joined with the audit log for
 * what changed; a run of identical events at one instant (a grouping run
 * attaching 13 entities) collapses into one row. Actors read as people.
 * Narrower than 45rem the column folds into a Filter button. The service
 * cannot limit the feed to the Cases time range yet — the note says so.
 */

type Filter = 'all' | CmTimelineKind

const KINDS: { key: Filter; label: string; count?: (c: CmWorkspace['counts']) => number }[] = [
  { key: 'all', label: 'All' },
  { key: 'signal', label: 'Signals', count: (c) => c.signals },
  { key: 'note', label: 'Notes', count: (c) => c.notes },
  { key: 'transition', label: 'Status' },
  { key: 'assignment', label: 'Assignment' },
  { key: 'audit', label: 'Changes' },
  { key: 'relation', label: 'Relations', count: (c) => c.relations },
  { key: 'evidence', label: 'Evidence', count: (c) => c.evidence },
  { key: 'itsm', label: 'ITSM' },
  { key: 'sla', label: 'SLA' },
  { key: 'system', label: 'System' },
]
const KIND_LABEL = Object.fromEntries(KINDS.map((k) => [k.key, k.label])) as Record<Filter, string>

const hhmm = (at: number | null) => (at === null ? '—' : new Date(at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }))
const kb = (n: number) => (n >= 1_048_576 ? `${(n / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`)
const shortSha = (s: string) => `${s.slice(0, 6)}…${s.slice(-4)}`

/** A kind's node shape (CSS, docs/cases.md §Timeline); a tint colors it. */
function Shape({ kind, tint }: { kind: Filter | CmTimelineEventKind; tint?: string }) {
  return <span className="cst-tl-shape" data-kind={kind} data-tint={tint} aria-hidden="true" />
}

export function TimelineTab({
  scope,
  caseId,
  ws,
  statuses,
  connections,
}: {
  scope: CmScope
  caseId: string
  ws: CmWorkspace
  statuses: CmStatus[]
  /** ITSM connection id → name, for `itsm:<id>` actors. */
  connections: Map<string, string>
}) {
  const [kind, setKind] = useState<Filter>('all')
  const [who, setWho] = useState<string | null>(null)
  const [folded, setFolded] = useState<Record<string, boolean>>({})
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  const [copied, setCopied] = useState<string | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const timelineQ = useCmTimeline(scope, caseId, kind === 'all' ? null : [kind])
  const auditQ = useCmAuditIndex(scope, caseId)
  const peopleQ = useCmPeople(scope)
  const people = peopleQ.data
  const signals = useMemo(() => new Map((ws.signals?.items ?? []).map((l) => [l.signalId, l])), [ws.signals])
  // The case's attached entities (the workspace's first 50): an entity attach reads `type · name`, not its id.
  const entities = useMemo(() => new Map((ws.entities?.items ?? []).map((l) => [l.entityId, l])), [ws.entities])
  const detailOf = (id: string) => {
    const entry = auditQ.data?.get(id)
    return auditEntity(entry, entities)?.label ?? auditDetail(entry)
  }
  const severity = useMemo(() => new Map([...signals].map(([id, l]) => [id, sevKey(l.severity)])), [signals])
  const categoryOf = (status: string) => statuses.find((s) => s.key === status)?.category

  const loaded = useMemo(() => (timelineQ.data?.pages ?? []).flatMap((p) => p.items), [timelineQ.data])
  const events = useMemo(() => (who === null ? loaded : loaded.filter((e) => (e.actor ?? '') === who)), [loaded, who])
  // A relation event names the other case by UUID only: its number, title and shared entities come from this case's relations.
  const relatedQ = useCmRelatedCases(scope, caseId, loaded.some((e) => e.kind === 'relation' && e.otherCaseId))
  const days = useMemo(() => timelineDays(collapseRuns(events)), [events])
  const actors = useMemo(() => {
    const list = actorCounts(loaded)
    return who !== null && !list.some((a) => a.actor === who) ? [...list, { actor: who, count: 0 }] : list
  }, [loaded, who])
  const name = (actor: string | null) => actorLabel(actor, people, connections)
  const related = (e: CmTimelineEvent) => (e.otherCaseId ? relatedQ.data?.get(e.otherCaseId) : undefined)
  const otherNumber = (e: CmTimelineEvent) => related(e)?.other.caseNumber ?? '—'
  const statusText = (key: string | null) => statusLabel(statuses, key ?? '—')

  const pickKind = (k: Filter) => {
    setKind(k)
    setOpen(new Set())
    setFiltersOpen(false)
  }
  const pickWho = (a: string | null) => {
    setWho(a)
    setOpen(new Set())
    setFiltersOpen(false)
  }

  /** What happened (the row's bold part) and its detail — plain text, also what Copy puts on the clipboard. */
  const describe = (row: TimelineRow): { title: string; detail: string | null; quote?: boolean } => {
    const e = row.events[0]!
    const n = row.events.length
    const signalTitle = (id: string | null) => signals.get(id ?? '')?.title ?? id ?? 'a signal'
    switch (e.kind) {
      case 'signal':
        return n > 1 ? { title: `${n} signals attached`, detail: row.events.map((x) => signalTitle(x.signalId)).join(' · ') } : { title: `Attached ${signalTitle(e.signalId)}`, detail: e.signalId }
      case 'note':
        return { title: n > 1 ? `${n} notes added` : 'Added a note', detail: e.text, quote: true }
      case 'transition':
        return { title: 'Changed status', detail: `${statusText(e.fromStatus)} → ${statusText(e.toStatus)}${e.disposition ? ` · ${dispositionLabel(e.disposition)}` : ''}` }
      case 'audit': {
        const fields = n > 1 ? null : editedFields(auditQ.data?.get(e.id))
        if (e.action === 'case.updated' && fields) return { title: `Edited ${fields.join(', ')}`, detail: null }
        const disposition = n > 1 || e.action !== 'case.disposition_changed' ? null : dispositionChangeText(auditQ.data?.get(e.id))
        if (disposition) return { title: disposition, detail: null }
        return { title: auditLabel(e.action, n), detail: n > 1 ? null : detailOf(e.id) }
      }
      case 'relation':
        // An unlinked pair names no other case (the service drops other_case_id).
        if (e.event === 'unlinked') return { title: 'Unlinked a related case', detail: e.relationType }
        return { title: `${e.event === 'dismissed' ? 'Dismissed' : 'Linked'} ${otherNumber(e)}`, detail: e.relationType }
      case 'assignment': {
        const said = n > 1 ? null : assignmentText(auditQ.data?.get(e.id))
        return said ?? { title: auditLabel(e.action, n), detail: null }
      }
      case 'itsm':
      case 'sla':
      case 'system':
        return { title: auditLabel(e.action, n), detail: n > 1 ? null : auditDetail(auditQ.data?.get(e.id)) }
      case 'other':
        return { title: e.action ? auditLabel(e.action, n) : e.rawKind, detail: e.action ? e.rawKind : null }
      case 'evidence':
        return {
          title: `${e.action === 'add' ? 'Uploaded' : e.action ? auditLabel(e.action) : 'Evidence'} ${e.fileName ?? ''}`.trim(),
          detail: [e.sizeBytes !== null ? kb(e.sizeBytes) : null, e.sha256 ? `sha256 ${shortSha(e.sha256)}` : null].filter(Boolean).join(' · ') || null,
        }
    }
  }

  const copy = (row: TimelineRow) => {
    const e = row.events[0]!
    const d = describe(row)
    const text = d.detail ? `${d.title} ${d.quote ? `“${d.detail}”` : d.detail}` : d.title
    void copyText(eventCopyLine(e.at, name(e.actor), text, caseLabel(ws.case))).then((ok) => setCopied(ok ? row.key : `!${row.key}`))
    window.setTimeout(() => setCopied(null), 1600)
  }

  const filters = (
    <nav className="cst-tl-rail" aria-label="Timeline filters">
      <div className="cst-tl-group" role="group" aria-label="Kind">
        <span className="hud-eyebrow">kind</span>
        {KINDS.map((k) => (
          <button key={k.key} type="button" className="cst-tl-kind" aria-pressed={kind === k.key} onClick={() => pickKind(k.key)}>
            <Shape kind={k.key} />
            <span className="cst-tl-kind-label">{k.label}</span>
            {k.count ? <span className="mono muted">{k.count(ws.counts)}</span> : null}
          </button>
        ))}
      </div>
      <div className="cst-tl-group" role="group" aria-label="Who">
        <span className="hud-eyebrow">who</span>
        <button type="button" className="cst-tl-kind" aria-pressed={who === null} onClick={() => pickWho(null)}>
          <Shape kind="all" />
          <span className="cst-tl-kind-label">Anyone</span>
        </button>
        {actors.map((a) => (
          <button key={a.actor} type="button" className="cst-tl-kind" aria-pressed={who === a.actor} onClick={() => pickWho(a.actor)} title={name(a.actor || null)}>
            <span className="cst-tl-who-mark" aria-hidden="true">
              {isSystemActor(a.actor || null) ? <Icon icon={Cog} size={12} /> : <Avatar name={name(a.actor)} size="sm" />}
            </span>
            <span className="cst-tl-kind-label">{name(a.actor || null)}</span>
            <span className="mono muted">{a.count}</span>
          </button>
        ))}
        <span className="hud-unit">counts from the {loaded.length} loaded events</span>
      </div>
    </nav>
  )

  const filtered = kind !== 'all' || who !== null
  let body: ReactNode
  if (timelineQ.isError) {
    body = (
      <div className="bui-alert bui-alert-error" role="alert">
        <span className="bui-alert-mark">✕</span>
        <span>
          <span className="bui-alert-title">Timeline failed to load</span>
          {timelineQ.error.message}
        </span>
        <Button variant="outline" size="sm" tone="neutral" onPress={() => void timelineQ.refetch()}>
          Retry
        </Button>
      </div>
    )
  } else if (timelineQ.isLoading) {
    body = (
      <ol className="cst-rail-list cst-tl-list" role="status" aria-label="Loading the timeline">
        {[1, 2, 3, 4, 5].map((k) => (
          <li key={k} className="cst-tl-ev cst-tl-skel">
            <span className="bui-skeleton" />
            <span className="bui-skeleton" />
          </li>
        ))}
      </ol>
    )
  } else if (!events.length) {
    body = filtered ? (
      <div className="cst-tl-empty">
        <b>
          {kind === 'all' ? 'No events' : `No ${KIND_LABEL[kind].toLowerCase()} events`}
          {who !== null ? ` by ${name(who || null)}` : ''}
        </b>
        <span className="muted">{who !== null ? `Who filters the ${loaded.length} loaded events only.` : 'Nothing of this kind on the case yet.'}</span>
        <Button variant="outline" size="sm" tone="neutral" onPress={() => { pickKind('all'); pickWho(null) }}>
          Clear filter
        </Button>
      </div>
    ) : (
      <div className="cst-tl-empty">
        <b>No events yet</b>
        <span className="muted">Signals, notes, status moves and edits on this case appear here as they happen.</span>
      </div>
    )
  } else {
    body = days.map((d) => {
      const isOpen = folded[d.key] ?? d.open
      const listId = `tl-day-${d.key}`
      return (
        <section key={d.key} className="cst-tl-day" aria-label={`${d.label}, ${d.count} events`}>
          <h3 className="cst-tl-dayhead">
            <button type="button" className="cst-tl-daybtn" aria-expanded={isOpen} aria-controls={isOpen ? listId : undefined} onClick={() => setFolded((f) => ({ ...f, [d.key]: !isOpen }))}>
              <Icon icon={ChevronRight} size={12} />
              {d.label}
              <span className="hud-unit">{d.count} events</span>
              {isOpen ? null : (
                <span className="cst-tl-kinds hud-unit" aria-hidden="true">
                  {Object.entries(d.kinds).map(([k, n]) => (
                    <span key={k}>
                      <Shape kind={k as CmTimelineKind} />
                      {n}
                    </span>
                  ))}
                </span>
              )}
            </button>
          </h3>
          {isOpen ? (
            <ol id={listId} className="cst-rail-list cst-tl-list">
              {d.rows.map((row) => {
                const e = row.events[0]!
                const n = row.events.length
                const text = describe(row)
                const expanded = open.has(row.key)
                const panelId = `tl-ev-${row.key}`
                const ticket = e.kind === 'note' && Boolean(e.actor && isTicketNote(e.actor))
                return (
                  <li key={row.key} className="cst-tl-ev" data-kind={e.kind} data-ticket={ticket || undefined}>
                    <span className="cst-tl-node">
                      <Shape kind={e.kind} tint={eventTint(row, severity, categoryOf)} />
                    </span>
                    <button
                      type="button"
                      className="cst-tl-row"
                      aria-expanded={expanded}
                      aria-controls={expanded ? panelId : undefined}
                      onClick={() => setOpen((x) => {
                        const next = new Set(x)
                        if (next.has(row.key)) next.delete(row.key)
                        else next.add(row.key)
                        return next
                      })}
                    >
                      <time className="mono cst-tl-time" title={e.at !== null ? absTime(e.at) : undefined}>
                        {hhmm(e.at)}
                      </time>
                      <span className="cst-tl-title">{text.title}</span>
                      {n > 1 ? <span className="sg-queue-n">{n}</span> : null}
                      {e.kind === 'transition' && n === 1 ? (
                        <span className="cst-tl-sub">
                          <span className="cst-tl-cat" data-category={categoryOf(e.fromStatus ?? '')}>{statusText(e.fromStatus)}</span> →{' '}
                          <span className="cst-tl-cat" data-category={categoryOf(e.toStatus ?? '')}>{statusText(e.toStatus)}</span>
                          {e.disposition ? <> · {dispositionLabel(e.disposition)}</> : null}
                        </span>
                      ) : text.detail ? (
                        <span className={text.quote ? 'cst-tl-sub cst-tl-quote' : 'cst-tl-sub'}>{text.quote ? `“${text.detail}”` : text.detail}</span>
                      ) : (
                        <span className="cst-tl-sub" />
                      )}
                      <span className="cst-tl-who">· {name(e.actor)}</span>
                    </button>
                    {expanded ? (
                      <div id={panelId} className="cst-tl-x">
                        <dl className="kv">
                          <dt>when</dt>
                          <dd className="mono">{e.at !== null ? absTime(e.at) : '—'}</dd>
                          <dt>who</dt>
                          <dd>
                            {name(e.actor)}
                            {e.actor && name(e.actor) !== e.actor ? <span className="mono muted"> {e.actor}</span> : null}
                          </dd>
                          {n === 1 ? <EventFacts e={e} scope={scope} signals={signals} statusText={statusText} entry={auditQ.data?.get(e.id)} relation={related(e)} /> : null}
                        </dl>
                        {e.kind === 'note' && n === 1 ? <p className="cst-note-body">{e.text}</p> : null}
                        {ticket ? <span className="hud-unit">Came in from a linked ticket.</span> : null}
                        {e.kind === 'audit' && e.action === 'case.updated' ? <span className="hud-unit">The service records which fields changed, not their values.</span> : null}
                        {n > 1 ? (
                          <ul className="cst-tl-items">
                            {row.events.map((x) => {
                              const ent = auditEntity(auditQ.data?.get(x.id), entities)
                              return (
                                <li key={x.id} className="mono" title={ent?.id}>
                                  {x.kind === 'signal' ? (signals.get(x.signalId ?? '')?.title ?? x.signalId) : (ent?.label ?? auditDetail(auditQ.data?.get(x.id)) ?? x.text ?? x.id)}
                                </li>
                              )
                            })}
                          </ul>
                        ) : null}
                        <span className="hud-row">
                          <Button variant="ghost" size="sm" tone="neutral" onPress={() => copy(row)}>
                            <Icon icon={Copy} size={12} /> Copy
                          </Button>
                          {copied === row.key || copied === `!${row.key}` ? (
                            <span className="hud-unit" role="status">
                              {copied === row.key ? 'Copied' : 'Copy failed'}
                            </span>
                          ) : null}
                        </span>
                      </div>
                    ) : null}
                  </li>
                )
              })}
            </ol>
          ) : null}
        </section>
      )
    })
  }

  return (
    <div className="cst-tl">
      {filters}

      <div className="cst-tl-feed">
        <div className="hud-row cst-tl-top">
          <DialogTrigger isOpen={filtersOpen} onOpenChange={setFiltersOpen}>
            <AriaButton className="bui-btn bui-btn-outline bui-btn-sm cst-tl-filterbtn">
              <Icon icon={SlidersHorizontal} size={14} /> Filter
              <span className="muted">
                · {KIND_LABEL[kind] === 'All' ? 'All kinds' : KIND_LABEL[kind]} · {who === null ? 'Anyone' : name(who || null)}
              </span>
            </AriaButton>
            <Popover className="pick-pop" placement="bottom start">
              <Dialog className="pick-dialog" aria-label="Timeline filters">
                {filters}
              </Dialog>
            </Popover>
          </DialogTrigger>
        </div>

        {body}

        {timelineQ.hasNextPage ? (
          <div className="cst-tl-more">
            <Button variant="ghost" size="sm" isDisabled={timelineQ.isFetchingNextPage} onPress={() => void timelineQ.fetchNextPage()}>
              {timelineQ.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** An event's own facts when it opens, by kind (the `kv` rows after when / who). */
function EventFacts({
  e,
  scope,
  signals,
  statusText,
  entry,
  relation,
}: {
  e: CmTimelineEvent
  scope: CmScope
  signals: Map<string, NonNullable<CmWorkspace['signals']>['items'][number]>
  statusText: (key: string | null) => string
  /** The audit entry behind an audit-backed event: what changed, who was assigned. */
  entry: CmAuditEntry | undefined
  relation: CmRelation | undefined
}) {
  const fields = editedFields(entry)
  const params = { clusterId: scope.clusterId, scopeId: scope.scopeId }
  switch (e.kind) {
    case 'signal': {
      const l = signals.get(e.signalId ?? '')
      return (
        <>
          <dt>detection</dt>
          <dd>{l?.title ?? '—'}</dd>
          <dt>severity</dt>
          <dd>{l?.severity ? <span className="cs-sev" data-sev={sevKey(l.severity)}>{sevKey(l.severity)}</span> : '—'}</dd>
          <dt>signal</dt>
          <dd>
            <span className="mono">{e.signalId ?? '—'}</span>{' '}
            {e.signalId ? (
              <Link to="/$clusterId/$scopeId/vantage/signals" params={params} search={{ signal: e.signalId }} className="bui-link">
                Open on Signals
              </Link>
            ) : null}
          </dd>
        </>
      )
    }
    case 'transition':
      return (
        <>
          <dt>from</dt>
          <dd>{statusText(e.fromStatus)}</dd>
          <dt>to</dt>
          <dd>{statusText(e.toStatus)}</dd>
          {e.disposition ? (
            <>
              <dt>disposition</dt>
              <dd>{dispositionLabel(e.disposition)}</dd>
            </>
          ) : null}
        </>
      )
    case 'audit':
      return fields ? (
        <>
          <dt>fields</dt>
          <dd className="mono">{fields.join(', ')}</dd>
        </>
      ) : null
    case 'relation': {
      const number = relation?.other.caseNumber
      return (
        <>
          <dt>case</dt>
          <dd>
            {number ? (
              <Link to="/$clusterId/$scopeId/vantage/cases/$caseId" params={{ ...params, caseId: number }} className="bui-link mono">
                {number}
              </Link>
            ) : (
              '—'
            )}
            {relation?.other.title ? <> {relation.other.title}</> : null}
          </dd>
          <dt>link</dt>
          <dd>{e.relationType === 'manual' ? 'manual — an analyst linked them' : e.relationType === 'auto' ? 'auto — the relations engine found shared entities' : (e.relationType ?? '—')}</dd>
          {relation?.entities.length ? (
            <>
              <dt>shared</dt>
              <dd>
                <span className="cs-rel-shared">
                  {relation.entities.map((x) => (
                    <span key={`${x.type}:${x.name}`} className="cs-rel-chip" title={`${x.type} ${x.name} — seen ${x.count} times`}>
                      <span className="muted">{x.type}</span> <span className="mono">{x.name}</span> <span className="muted">×{x.count}</span>
                    </span>
                  ))}
                </span>
              </dd>
            </>
          ) : null}
        </>
      )
    }
    case 'evidence':
      return (
        <>
          <dt>file</dt>
          <dd className="mono">{e.fileName ?? '—'}</dd>
          <dt>size</dt>
          <dd className="mono">{e.sizeBytes !== null ? kb(e.sizeBytes) : '—'}</dd>
          <dt>sha256</dt>
          <dd className="mono">{e.sha256 ?? '—'}</dd>
        </>
      )
    case 'assignment': {
      const d = entry?.detail ?? {}
      const text = (v: unknown) => (typeof v === 'string' && v ? v : '—')
      return (
        <>
          <dt>assignee</dt>
          <dd>{e.action === 'case.unassigned' ? 'Unassigned' : text(d.assignee)}</dd>
          <dt>before</dt>
          <dd>{text(d.previous_assignee)}</dd>
        </>
      )
    }
    case 'itsm':
    case 'sla':
    case 'system':
    case 'other':
      return e.action ? (
        <>
          <dt>action</dt>
          <dd className="mono">{e.action}</dd>
        </>
      ) : null
    case 'note':
      return null
  }
}
